import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaNotesMedical, FaChevronDown, FaChevronUp, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { goReserve, goBackSmart } from '../../utils/navigation';

interface FaqAgaFemaleProps {
  ipAllowed: boolean;
}

/** ---------- 型定義（ファイル構造の必要部分） ---------- */
type Section = { title: string; paras?: string[]; items?: string[]; note?: string };
type QA = { q: string; a: string[] | string };

type PageMeta = {
  title?: string;
  back?: string;
  cta_menu?: string;
  cta_reserve?: string;
  disclaimer?: string;
};

type AgaFemaleDoc = {
  page?: PageMeta;
  overview?: Section;
  cause?: Section;
  treatment?: { title: string; items?: string[] };
  qa?: QA[];
};

type FaqContentFile = {
  faq?: {
    aga_female?: AgaFemaleDoc;
  };
};

/** ---------- 公開パス補助（Vite 子パス対応） ---------- */
const dataUrl = (filename: string) => {
  // eslint-disable-next-line
  const base = process.env.PUBLIC_URL || '/';
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return `${prefix}data/${filename}`;
};

/** ---------- /public/data/faq_*.json 読み込み ---------- */
async function loadAgaFemale(lang: 'ja' | 'zh'): Promise<AgaFemaleDoc | null> {
  try {
    const res = await fetch(dataUrl(`faq_${lang}.json`), { cache: 'no-store' });
    if (!res.ok) return null;
    const json = (await res.json()) as FaqContentFile;
    return json?.faq?.aga_female || null;
  } catch {
    return null;
  }
}

const FaqAgaFemale: React.FC<FaqAgaFemaleProps> = ({ ipAllowed }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang: 'ja' | 'zh' = i18n.language === 'zh' ? 'zh' : 'ja';

  const [doc, setDoc] = useState<AgaFemaleDoc | null>(null);
  const [loading, setLoading] = useState(true);

  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const toggle = (idx: number) => setOpenIdx(cur => (cur === idx ? null : idx));

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      const data = await loadAgaFemale(lang);
      if (!mounted) return;
      setDoc(data);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [lang]);

  useEffect(() => {
    setOpenIdx(0);
  }, [doc]);

  const page: Required<PageMeta> = {
    title: doc?.page?.title || (lang === 'ja' ? '女性AGA（FAGA）についてのFAQ' : '女性 AGA（FAGA）常见问题'),
    back: doc?.page?.back || (lang === 'ja' ? '戻る' : '返回'),
    cta_menu: doc?.page?.cta_menu || (lang === 'ja' ? 'AGA（女性）メニューへ' : '前往 AGA（女性）菜单'),
    cta_reserve: doc?.page?.cta_reserve || (lang === 'ja' ? 'オンラインで予約する' : '在线预约'),
    disclaimer:
      doc?.page?.disclaimer ||
      (lang === 'ja'
        ? '※ 本ページは一般的な情報の提供を目的としています。診断や治療の最終判断は必ず医師の診察に基づき行ってください。'
        : '※ 本页面仅提供一般性信息，诊断与治疗的最终判断请务必依据医师面诊。'),
  };

  const OVERVIEW = doc?.overview || { title: '', paras: [] };
  const CAUSE = doc?.cause || { title: '', paras: [] };
  const TREATMENT = doc?.treatment || { title: '', items: [] };

  const QA_LIST: QA[] = useMemo(() => (Array.isArray(doc?.qa) ? (doc!.qa as QA[]) : []), [doc]);

  // JSON-LD
  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: QA_LIST.slice(0, 25).map((qa) => ({
        '@type': 'Question',
        name: qa.q,
        acceptedAnswer: { '@type': 'Answer', text: Array.isArray(qa.a) ? qa.a.join('\n') : qa.a },
      })),
    }),
    [QA_LIST]
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-rose-50/70 font-sans">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-7 md:p-9">
            <h1 className="text-2xl font-semibold text-slate-900">
              {lang === 'ja' ? '読み込み中…' : '加载中…'}
            </h1>
          </div>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-rose-50/70 font-sans">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="bg-white border border-amber-200 rounded-3xl shadow-sm p-7 md:p-9">
            <h1 className="text-2xl font-semibold text-amber-800">
              {lang === 'ja' ? 'データが見つかりません' : '未找到数据'}
            </h1>
            <p className="mt-2 text-amber-700">
              {lang === 'ja'
                ? 'public/data/faq_ja.json / faq_zh.json に faq.aga_female ブロックを配置してください。'
                : '请在 public/data/faq_ja.json / faq_zh.json 中提供 faq.aga_female 区块。'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 bg-rose-50/70 font-sans" translate="no">
      <Helmet>
        <title>{page.title} - Melodis Clinic</title>
        <meta name="robots" content="noindex, nofollow" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="container mx-auto px-4 max-w-5xl">
        <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 md:p-9">
          {/* Back */}
          <div className="mb-3">
            <button
              type="button" onClick={() => goBackSmart(navigate, '/faq')}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm"
            >
              <FaArrowLeft className="w-4 h-4" />
              {page.back}
            </button>
          </div>

          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-rose-100 p-3">
              <FaNotesMedical className="text-rose-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
              <span className="text-rose-400 mr-2">│</span>
              {page.title}
            </h1>
          </div>

          {/* Overview */}
          <section className="rounded-2xl border border-rose-100 bg-rose-50 p-6 md:p-7 mb-8">
            <h2 className="text-2xl font-semibold text-rose-700 mb-3">{OVERVIEW.title}</h2>
            <div className="space-y-3 text-slate-800 leading-relaxed">
              {(OVERVIEW.paras || []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </section>

          {/* Cause */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-2">{CAUSE.title}</h3>
            <ul className="list-disc pl-5 space-y-1 text-slate-800">
              {(CAUSE.paras || []).map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </section>

          {/* Treatment */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-2">{TREATMENT.title}</h3>
            <ul className="list-disc pl-5 space-y-1 text-slate-800">
              {(TREATMENT.items || []).map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </section>

          {/* Q&A */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">
              {lang === 'ja' ? 'よくある質問' : '常见问题'}
            </h3>
            <div className="space-y-3">
              {QA_LIST.map((qa, idx) => {
                const isOpen = openIdx === idx;
                return (
                  <div key={idx} className="border border-slate-200 rounded-xl bg-slate-50">
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => toggle(idx)}
                      className="w-full flex items-start justify-between gap-4 px-4 py-3 text-left"
                    >
                      <span className="font-medium text-slate-900">{qa.q}</span>
                      {isOpen ? (
                        <FaChevronUp className="mt-1 shrink-0 text-slate-400" />
                      ) : (
                        <FaChevronDown className="mt-1 shrink-0 text-slate-400" />
                      )}
                    </button>
                    <div className={`px-4 pb-4 ${isOpen ? '' : 'hidden'}`}>
                      {Array.isArray(qa.a) ? (
                        <ul className="list-disc pl-5 space-y-1 text-slate-800">
                          {(qa.a as string[]).map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-slate-800">{qa.a as string}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-slate-500 text-xs mt-4">{page.disclaimer}</p>
          </section>

          {/* CTA */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => navigate('/menu/aga-female')}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-slate-700 hover:bg-slate-50"
            >
              {page.cta_menu}
            </button>
            <button
              disabled
              className="inline-block bg-gray-400 text-white px-8 py-3 rounded-full font-semibold shadow-sm cursor-not-allowed grayscale"
            >
              {lang === 'ja' ? '準備中' : '筹备中'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqAgaFemale;
